import { useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Select, { StylesConfig, components } from 'react-select';
import { List, RowComponentProps } from "react-window";

export interface ColorOption {
    value: string | number,
    label: string | number,
    color: string,
}

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
  },
});

function RowComponent({
    index,
    style,
    comp,
}: RowComponentProps<{
    comp: any,
}>) {
    return (
        <div style={style}>{comp[index]}</div>
    )
}

const MenuList = (props:any) => {
    const itemHeight = 37;
    const {  children, maxHeight } = props;
    // const [value] = getValue();
    // const initialOffset = options.indexOf(value) * itemHeight;
    const height = Math.min(maxHeight, children.length * itemHeight);

    return (
        <div style={{ height }}>
            <List
                rowComponent={RowComponent}
                rowCount={children.length}
                rowHeight={itemHeight}
                rowProps={{
                    comp: children,
                }}
            />
        </div>
    )
}



export default function ColoredSelectFields({
    label,
    id,
    name,
    value,
    options,
    handleChange,
    menuPortalTarget,
    disabled,
}: {
    label?: string,
    id: string,
    name: string,
    value: ColorOption | null,
    options: ColorOption[],
    handleChange: (e: ColorOption, name: string, index?: number) => void,
    menuPortalTarget?: HTMLElement | null,
    disabled?: boolean,
}) {

    const theme = useTheme();
    const selectRef = useRef<HTMLDivElement | null>(null);
    const [ maxWidth, setMaxWidth ] = useState(200);

    useEffect(() => {
        if (selectRef.current) {
            const width = selectRef.current.offsetWidth;
            setMaxWidth(width - 24);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (selectRef.current) {
            setMaxWidth(selectRef.current.offsetWidth - 24);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const Option = (props: any) => {
        const { children } = props;
        return (
            <components.Option {...props}>
                <div style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: `${maxWidth}px`  // adjust as needed
                }}>
                    {children}
                </div>
            </components.Option>
        )
    }

    const colorStyles: StylesConfig<ColorOption> = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? theme.palette.primary.main : '#C1C7CD',
            width: '100%',
            minHeight: 44,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: theme.palette.primary.main,
                cursor: 'pointer'
            }
        }),
        valueContainer: (baseStyles, state) => ({
            ...baseStyles,
            padding: '0 12px',
        }),
        menuList: (baseStyles, state) => ({
            ...baseStyles,
            color: '#35424A',
        }),
        menu: (baseStyles, state) => ({
            ...baseStyles,
            zIndex: 50,
        }),
        option: (baseStyles, { data, isSelected, isFocused }) => ({
            ...baseStyles,
            ...dot(data.color),
            backgroundColor: isSelected ? theme.palette.secondary.main : isFocused ? theme.palette.secondary.light : 'white',
            color: '#35424A',
            '&:hover': {
                cursor: 'pointer',
            }
        }),
        input: (baseStyles) => ({...baseStyles, ...dot()}),
        placeholder: (baseStyles) => ({...baseStyles, ...dot()}),
        singleValue: (baseStyles, { data }) => ({...baseStyles, ...dot(data.color)}),
    }


    return (
        <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
            {label !== undefined && <label htmlFor={id} className={`text-[12px] font-medium`}>{label}</label>}
            <div className="w-full" ref={selectRef}>
                <Select 
                    isDisabled={disabled}
                    menuPortalTarget={menuPortalTarget}
                    id={id} 
                    options={options}
                    components={{ MenuList, Option }}
                    value={value} 
                    onChange={(e:any) => handleChange(e, name)} 
                    className="w-full"
                    menuPlacement="auto"
                    styles={colorStyles}
                />
            </div>
        </div>
    )
}